import React, { useState, useRef, useEffect } from "react";
import { Bot, Send, Sparkles, ShieldCheck, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const SUGGESTIONS_BY_MODULE = {
    executive: [
        "Summarise this month's production versus plan.",
        "What are the top 3 risks I should present at Board next week?",
        "Explain the change in cash cost.",
    ],
    mining: ["Which pit is bottlenecking cycle time?", "Any equipment causing loss of production this week?"],
    plant: ["Explain the Cu recovery trend.", "What actions could lift recovery by 1%?"],
    maintenance: ["Which assets should we prioritise for the next shutdown?", "Any bad-actor pumps?"],
    safety: ["Summarise HiPo incidents this quarter.", "What is our leading indicator trend?"],
    environment: ["Any environmental exceedances that need Board escalation?"],
    tailings: ["Which piezometers are trending toward trigger levels?"],
    ai: ["Summarise the platform status.", "Draft a 3-line executive briefing."],
};

export function AIAdvisor({ module = "executive", context, embedded = false, onClose }) {
    const [messages, setMessages] = useState([
        {
            role: "assistant",
            content:
                "I am the KCC360 Executive AI Advisor. Ask me about production, safety, maintenance risk, environmental compliance, or any module. All recommendations I make must be validated by an authorised role before action.",
            sources: [],
        },
    ]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const scrollerRef = useRef(null);

    useEffect(() => {
        scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, sending]);

    async function send(text) {
        const message = (text ?? input).trim();
        if (!message || sending) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", content: message }]);
        setSending(true);
        try {
            const { data } = await api.post("/ai/chat", { message, module, context, session_id: sessionId });
            setSessionId(data.session_id);
            setMessages((m) => [...m, { role: "assistant", content: data.reply, sources: data.sources || [] }]);
        } catch (e) {
            const err = formatApiErrorDetail(e.response?.data?.detail) || e.message;
            setMessages((m) => [...m, { role: "assistant", content: `⚠︎ ${err}`, sources: [] }]);
        } finally {
            setSending(false);
        }
    }

    const suggestions = SUGGESTIONS_BY_MODULE[module] || SUGGESTIONS_BY_MODULE.executive;

    return (
        <div className={`flex flex-col h-full ${embedded ? "" : "kcc-panel"}`} data-testid="ai-advisor">
            <header className="flex items-center gap-3 p-4 border-b border-white/5">
                <div className="w-9 h-9 rounded-md flex items-center justify-center bg-kcc-purple/15 border border-kcc-purple/30">
                    <Bot className="w-5 h-5 text-kcc-purple" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-display text-sm font-semibold text-white">Executive AI Advisor</div>
                    <div className="text-[10.5px] kcc-text-silver flex items-center gap-1.5">
                        <ShieldCheck className="w-3 h-3 text-kcc-green" />
                        Governed · RAG-backed · Human-in-the-loop
                    </div>
                </div>
                {onClose && (
                    <button className="text-kcc-silver hover:text-white" onClick={onClose} data-testid="ai-close">
                        <X className="w-4 h-4" />
                    </button>
                )}
            </header>

            <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
                {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div
                            className={`max-w-[86%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed ${
                                m.role === "user"
                                    ? "bg-kcc-cobalt/15 border border-kcc-cobalt/25 text-white"
                                    : "bg-white/[0.03] border border-white/8 text-kcc-silver"
                            }`}
                            data-testid={`ai-msg-${m.role}-${i}`}
                        >
                            <div className="whitespace-pre-wrap">{m.content}</div>
                            {m.sources?.length > 0 && (
                                <div className="mt-2 pt-2 border-t border-white/5 text-[10.5px] kcc-text-dim flex flex-wrap gap-1.5">
                                    <Sparkles className="w-3 h-3 text-kcc-purple" />
                                    {m.sources.map((s) => (
                                        <span key={s} className="px-1.5 py-0.5 rounded border border-white/10">{s}</span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {sending && (
                    <div className="flex justify-start">
                        <div className="bg-white/[0.03] border border-white/8 rounded-lg px-3.5 py-2.5 text-sm kcc-text-silver">
                            <span className="inline-flex gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-kcc-silver animate-pulse" />
                                <span className="w-1.5 h-1.5 rounded-full bg-kcc-silver animate-pulse" style={{ animationDelay: "150ms" }} />
                                <span className="w-1.5 h-1.5 rounded-full bg-kcc-silver animate-pulse" style={{ animationDelay: "300ms" }} />
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {messages.length <= 1 && (
                <div className="px-4 pb-2 flex flex-wrap gap-1.5">
                    {suggestions.map((s) => (
                        <button
                            key={s}
                            onClick={() => send(s)}
                            className="text-[11px] px-2.5 py-1.5 rounded-full border border-white/10 hover:border-kcc-purple/50 hover:bg-kcc-purple/10 text-kcc-silver hover:text-white transition-colors"
                            data-testid="ai-suggestion"
                        >
                            {s}
                        </button>
                    ))}
                </div>
            )}

            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    send();
                }}
                className="p-3 border-t border-white/5 flex items-end gap-2"
            >
                <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            send();
                        }
                    }}
                    placeholder="Ask the Executive AI Advisor…"
                    rows={1}
                    className="flex-1 bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-kcc-purple/50 resize-none max-h-32"
                    data-testid="ai-input"
                />
                <button
                    type="submit"
                    disabled={sending || !input.trim()}
                    className="bg-kcc-copper hover:bg-kcc-copper-light disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-md px-3 py-2 transition-colors"
                    data-testid="ai-send"
                >
                    <Send className="w-4 h-4" />
                </button>
            </form>
        </div>
    );
}
